import { asyncOperation, clientRepo, useCollectionBind, useUser } from "@zickko/client";
import { EmailKey, firestorePaths, InboundEmail, OutboundEmail } from "@zickko/shared";
import { useOrg } from "../providers/OrgProvider";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { InboundEmailViewWidget } from "../widgets/email/InboundEmailViewWidget";
import { TrashEmailListWidget } from "../widgets/email/TrashEmailListWidget";
import { TrashEmail } from "@zickko/shared/src/models/TrashEmail";
import { BookPlus, Clock3, CornerUpLeft, CornerUpRight, EllipsisVerticalIcon, ReplyAll, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { OutboundEmailViewWidget } from "../widgets/email/OutboundEmailViewWidget";

export const TrashPage = () => {
  const { org } = useOrg();
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedEmail, setSelectedEmail] = useState<TrashEmail>();
  const emailId = searchParams.get("emailId");

  const { list: emailList = [] } = useCollectionBind(firestorePaths.trash, {
    key: {
      orgId: org.id,
      uid: user.uid,
    },
    orderByField: "uid",
    orderByDirection: "desc",
  });

  useEffect(() => {
    if (!selectedEmail && emailList.length > 0) {
      const firstEmail = emailList[0];
      setSearchParams({ emailId: firstEmail.emailId });
      setSelectedEmail(firstEmail);
    } else if (selectedEmail) {
      setSearchParams({ emailId: selectedEmail.emailId });
    }
  }, [selectedEmail, emailList, setSearchParams, setSelectedEmail]);

  if (emailList === undefined) {
    return <div>Loading emails...</div>;
  }

  const onDeleteEmailFromTrash = () => {
    asyncOperation({
      fn: () => {
        const emailKey: EmailKey = { emailId: emailId, orgId: selectedEmail.email.orgId, uid: selectedEmail.email.uid };
        return clientRepo.trashEmail.remove(emailKey);
      },
      onSuccess: () => {
        toast.success("Email Deleted Successfully");
      },
      onError(e) {
        toast.error("Error in deleting email.");
      },
    });
  };

  const onRestoreEmailFromTrash = () => {
    asyncOperation({
      fn: async () => {
        const emailKey: EmailKey = { emailId: emailId, orgId: selectedEmail.email.orgId, uid: selectedEmail.email.uid };
        const trashEmail = await clientRepo.trashEmail.get(emailKey);
        if (!trashEmail) {
          throw new Error("Email not found in trash.");
        }
        switch (trashEmail.type) {
          case "InboundEmail":
            await clientRepo.inboundEmail.set(emailKey, trashEmail.email as InboundEmail);
            break;
          case "OutboundEmail":
            await clientRepo.outboundEmail.set(emailKey, trashEmail.email as OutboundEmail);
            break;
          default:
            throw new Error("Unsupported email type.");
        }

        await clientRepo.trashEmail.remove(emailKey);
      },
      onSuccess: () => {
        toast.success("Email Successfully restored from Trash");
      },
      onError(e) {
        toast.error("Error in restoring email.");
      },
    });
  };

  return (
    <div className="inbox-page">
      <div className="email-list-widget">
        <div className="email-list-header">
          <div className="inbox-heading">Trash</div>
          <div className="switch-button">
            <div className={`all-mail`}>All Mail</div>
          </div>
        </div>
        <TrashEmailListWidget emailList={emailList} onSelectEmail={(email) => setSelectedEmail(email)} />
      </div>

      <div className="email-view-widget">
        <div className="email-action-items">
          <div className="actions">
            <Trash2 onClick={onDeleteEmailFromTrash} size={18} />
            <BookPlus onClick={onRestoreEmailFromTrash} size={18} />
            <div className="vertical-line"> </div>
            <Clock3 size={18} />
          </div>
          <div className="reply-actions">
            <CornerUpLeft size={18} />
            <ReplyAll size={18} />
            <CornerUpRight size={18} />
            <div className="vertical-line"> </div>
            <EllipsisVerticalIcon size={18} />
          </div>
        </div>
        {selectedEmail?.type === "InboundEmail" ? (
          <InboundEmailViewWidget email={selectedEmail?.email as InboundEmail} />
        ) : (
          <OutboundEmailViewWidget email={selectedEmail?.email as OutboundEmail} />
        )}
      </div>
    </div>
  );
};
